import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Phone } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, stagger, item } from "@/components/Reveal";
import { ServiceAreaMap } from "@/components/ServiceAreaMap";
import { FAQ } from "@/components/FAQ";
import { useLeadModal } from "@/context/LeadModalProvider";
import { LOCATIONS } from "@/data/locations";
import { CONTACT } from "@/data/site";
import { useSEO, faqSchema, breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { PAGE_META, AREA_FAQS } from "@/data/page_meta";

export default function ServiceAreas() {
  const { openQuote } = useLeadModal();
  useSEO({
    ...PAGE_META.serviceAreas,
    jsonLd: [
      webPageSchema({ path: "/service-areas", name: PAGE_META.serviceAreas.title, description: PAGE_META.serviceAreas.description, type: "CollectionPage" }),
      breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Service Areas", path: "/service-areas" }]),
      faqSchema(AREA_FAQS),
    ],
  });

  return (
    <div data-testid="service-areas-page">
      <section className="pt-36 pb-14 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(95,165,68,0.10),transparent_45%)]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4">Service Areas</p>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter text-navy leading-[0.92] max-w-4xl">
              Green Cleaning across the DMV.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              From our Alexandria office we serve homes and businesses throughout Northern Virginia, Washington DC,
              and nearby Maryland. Find your city below for local details.
            </p>
            <button onClick={openQuote} data-testid="areas-call-btn"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-leaf text-white font-medium hover:scale-105 transition-transform mt-8">
              <Phone className="w-4 h-4" /> Get Instant Quote
            </button>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {LOCATIONS.map((l) => (
              <motion.div key={l.slug} variants={item}>
                <Link to={`/service-areas/${l.slug}`} data-testid={`area-card-${l.slug}`}
                  className="group flex items-start gap-4 bg-white rounded-3xl p-6 border border-border hover:border-leaf/40 hover:-translate-y-1 transition-all duration-300 h-full">
                  <span className="w-11 h-11 shrink-0 rounded-2xl bg-leaf/10 flex items-center justify-center group-hover:bg-leaf transition-colors">
                    <MapPin className="w-5 h-5 text-leaf group-hover:text-white transition-colors" />
                  </span>
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-navy group-hover:text-leaf transition-colors">{l.name}, {l.state}</h2>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{l.neighborhoods.slice(0, 4).join(" · ")}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-leaf mt-3">View area <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><ServiceAreaMap /></Reveal>
        </div>
      </section>

      <FAQ items={AREA_FAQS} title="Service Area FAQs" />
    </div>
  );
}
